/**
 * The one place smooth scrolling and the animation runtime are started, and the one place
 * they are torn down.
 *
 * ## Why it is global
 *
 * Lenis replaces the page's scrolling. Two instances would fight over the same scroll
 * position, and GSAP's ScrollTrigger measures against exactly one scroller — so "the
 * component that needs scroll animation starts its own" is not a smaller version of this,
 * it is a broken one. The instance is therefore owned here, above the router, for the life
 * of the session.
 *
 * ## Why almost nothing is imported at the top
 *
 * GSAP and Lenis are pulled in with `import()` inside effects. A screen that decides
 * against motion — the television, a reduced-motion setting, the player — never fetches
 * either. That is what keeps the Tizen widget from carrying an animation engine it is
 * forbidden from using, and it is why `runtime` in the context starts as `null` rather
 * than being a plain import.
 *
 * ## One RAF loop, not two
 *
 * Lenis and GSAP each want to drive a `requestAnimationFrame` loop. Left alone they run
 * separately, and ScrollTrigger then reads a scroll position Lenis wrote *after* the last
 * measurement — the symptom is a pinned element that trails the content by a frame and
 * jitters. The fix is the documented one: Lenis is stepped from GSAP's ticker, so there is
 * a single clock and a fixed order — Lenis writes, then ScrollTrigger reads.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDeviceProfile } from '../device/useDeviceProfile';
import { MotionContext, type MotionApi } from './MotionContext';
import { decideMotion } from './decideMotion';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';
import { loadMotionRuntime, type MotionRuntime } from './runtime';
import type { Lenis } from './lenisRuntime';

/**
 * GSAP's shipped default, restored on teardown.
 *
 * The ticker normally throttles itself after a long frame, which is right for a page whose
 * animations are decorative and wrong for one where the ticker also drives the scroll
 * position — there it reads as the page seizing mid-gesture. Turning it off is part of
 * running Lenis, so turning it back on is part of stopping.
 */
const LAG_SMOOTHING_DEFAULT = [500, 33] as const;

export function MotionProvider({ children }: { children: React.ReactNode }) {
  const device = useDeviceProfile();
  const reducedMotion = usePrefersReducedMotion();
  const { pathname } = useLocation();

  const decision = useMemo(
    () => decideMotion({ device, reducedMotion, pathname }),
    [device, reducedMotion, pathname],
  );

  const [runtime, setRuntime] = useState<MotionRuntime | null>(null);
  const lenisRef = useRef<Lenis | null>(null);

  /* GSAP, fetched the first time any screen wants it and kept thereafter. */
  useEffect(() => {
    if (!decision.animate) return;
    let cancelled = false;
    void loadMotionRuntime().then((loaded) => {
      if (!cancelled) setRuntime(loaded);
    });
    return () => {
      cancelled = true;
    };
  }, [decision.animate]);

  /* Smooth scrolling. */
  useEffect(() => {
    if (!decision.smooth) return;

    let cancelled = false;
    let teardown: (() => void) | null = null;

    void (async () => {
      const [{ Lenis }, { gsap, ScrollTrigger }] = await Promise.all([
        import('./lenisRuntime'),
        loadMotionRuntime(),
      ]);
      // The effect was cleaned up while the chunks were in flight — a fast route change,
      // or StrictMode's deliberate double-mount in development. Creating the instance now
      // would leave one running with nobody holding a reference to stop it.
      if (cancelled) return;

      const lenis = new Lenis({
        // Kept short. This is a media library where scrolling is a means of finding
        // something, not a scrollytelling page where it is the experience; a long glide
        // makes a list of six hundred films feel further away, not more refined.
        duration: 0.9,
        smoothWheel: true,
        // Left alone on touch. Mobile browsers hand momentum scrolling to the compositor,
        // and taking it back into JavaScript trades a native-feeling gesture for a worse
        // imitation that also drops frames while the poster images decode.
        syncTouch: false,
        // The ticker below is the clock; Lenis must not start a second one.
        autoRaf: false,
      });
      lenisRef.current = lenis;

      const onScroll = () => ScrollTrigger.update();
      lenis.on('scroll', onScroll);

      // GSAP's ticker reports seconds; Lenis wants milliseconds.
      const tick = (time: number) => lenis.raf(time * 1000);
      gsap.ticker.add(tick);
      gsap.ticker.lagSmoothing(0);

      teardown = () => {
        lenis.off('scroll', onScroll);
        gsap.ticker.remove(tick);
        gsap.ticker.lagSmoothing(...LAG_SMOOTHING_DEFAULT);
        lenis.destroy();
        lenisRef.current = null;
      };
    })();

    return () => {
      cancelled = true;
      teardown?.();
    };
  }, [decision.smooth]);

  /*
   * A new screen means new measurements.
   *
   * Deliberately *not* a scroll-to-top: the app has never done that on navigation, and
   * quietly adding it here would be a behaviour change smuggled in under an animation
   * task. What is fixed is only what Lenis breaks — it caches the document height, so
   * arriving at a shorter screen leaves it with a scroll limit that no longer exists, and
   * the page can be scrolled into empty space with no way back. ScrollTrigger caches start
   * and end positions for the same reason.
   *
   * Both are measured a frame late, on purpose. Refreshing in the same tick measures the
   * screen that is leaving.
   */
  useEffect(() => {
    if (!lenisRef.current && !runtime) return;
    const id = window.requestAnimationFrame(() => {
      lenisRef.current?.resize();
      runtime?.ScrollTrigger.refresh();
    });
    return () => window.cancelAnimationFrame(id);
  }, [pathname, runtime]);

  const value = useMemo<MotionApi>(
    () => ({
      animate: decision.animate,
      smooth: decision.smooth,
      runtime,
      getLenis: () => lenisRef.current,
    }),
    [decision.animate, decision.smooth, runtime],
  );

  return <MotionContext.Provider value={value}>{children}</MotionContext.Provider>;
}
